import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Me, PublicConfig } from '@medicail/shared';
import { apiFetch, ApiRequestError } from '@/lib/http';
import { getAuthToken, setTokenProvider, setUnauthorizedHandler } from '@/lib/authToken';
import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';
import { cognitoSignOut, cognitoToken, configureCognito } from './cognito';
import { devSignOut, devToken } from './devAuth';

function setupAdapter(config: PublicConfig) {
  if (config.authMode === 'cognito' && config.cognito) {
    configureCognito(config.cognito);
    setTokenProvider(cognitoToken);
  } else {
    setTokenProvider(devToken);
  }
}

export function AuthProvider({ config, children }: { config: PublicConfig; children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ status: AuthStatus; me: Me | null }>({ status: 'loading', me: null });
  const [notice, setNotice] = useState<string | null>(null);
  const mode = config.authMode;

  // Configure synchronously so the very first request already has a token provider.
  useMemo(() => setupAdapter(config), [config]);

  const adapterSignOut = useCallback(async () => {
    if (mode === 'cognito') await cognitoSignOut();
    else devSignOut();
  }, [mode]);

  const refresh = useCallback(async () => {
    const token = await getAuthToken();
    if (!token) {
      setState({ status: 'unauthenticated', me: null });
      return;
    }
    try {
      const me = await apiFetch<Me>('/me');
      setState({ status: 'authenticated', me });
    } catch (err) {
      if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
        if (err.status === 403) setNotice('This account is not allowed to use MedicAiL. Please contact support.');
        await adapterSignOut();
      }
      setState({ status: 'unauthenticated', me: null });
    }
  }, [adapterSignOut]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await adapterSignOut();
    queryClient.clear();
    setState({ status: 'unauthenticated', me: null });
  }, [adapterSignOut, queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      void adapterSignOut().then(() => {
        queryClient.clear();
        setState((s) => (s.status === 'authenticated' ? { status: 'unauthenticated', me: null } : s));
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [adapterSignOut, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      me: state.me,
      status: state.status,
      signOut,
      refresh,
      notice,
      clearNotice: () => setNotice(null),
      mode,
    }),
    [state, signOut, refresh, notice, mode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
